import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalCloseButton,
    VStack,
    Text,
    Alert,
    AlertIcon,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Flex,
    useToast,
    HStack,
    Heading,
    Box,
    Icon,
    IconButton,
    Skeleton,
    Badge,
    Link
} from '@chakra-ui/react';
import { MdCheck, MdEdit } from 'react-icons/md';
import { TbCopy } from 'react-icons/tb';
import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useGetEms, type GetEmsResponse } from '../hooks/useGetEms';
import { useUpdateEms } from '../hooks/useUpdateEms';
import type { OrderDetailResponse } from '../hooks/useGetOrderDetail';
import { formatPrice } from '../functions';
import EditLocationModal from './EditLocationModal';
import PartType from '../components/PartType';
import PartBayButton from '../components/PartBayButton';
import QuotesNotSubmitted from '../components/QuotesNotSubmitted';
import useCopyToClipboard from '../hooks/useCopyToClipboard';
import CopyTooltip from '../components/CopyTooltip';
import { SegmentedControl } from '../ui';

interface EMSDetailModalProps {
    isOpen: boolean;
    onClose: () => void;
    emsId: string;
    /** Hide Send to Marketplaces (e.g. Quoting tab quote-history view). */
    hideSubmit?: boolean;
}

const EMSDetailModal = ({ isOpen, onClose, emsId, hideSubmit = false }: EMSDetailModalProps) => {
    const navigate = useNavigate();
    const toast = useToast();
    const { data, isLoading, error } = useGetEms(emsId, isOpen);
    const updateEms = useUpdateEms();
    const queryClient = useQueryClient();
    const [isEditLocationModalOpen, setIsEditLocationModalOpen] = useState(false);
    const [showPartsOnly, setShowPartsOnly] = useState(false);
    const { copy: copyText, isCopied } = useCopyToClipboard();

    const filteredParts = useMemo(() => {
        if (!data) return [];
        if (!showPartsOnly) return data.parts;
        return data.parts.filter(p => p.partNumber && p.listPrice > 0);
    }, [data, showPartsOnly]);

    const shouldHideSubmit = hideSubmit || data?.isCurrentOrderEms === false;
    const showMarketplaceBadges = data?.status === 'submitted';
    const showSubmitActions = data && !error && (showMarketplaceBadges || !shouldHideSubmit);

    const formatDate = (date: Date) => {
        return new Date(date).toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: '2-digit'
        });
    };

    const formatTime = (date: Date) => {
        return new Date(date).toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZoneName: 'short'
        });
    };

    const copyToClipboard = async (text: string, key: string, label: string = 'VIN', e?: React.MouseEvent) => {
        const ok = await copyText(text, key, e ? { x: e.clientX, y: e.clientY } : null);
        if (!ok) {
            toast({
                title: 'Copy Failed',
                description: `Failed to copy ${label} to clipboard`,
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const applyOptimisticSubmission = useCallback(() => {
        // Reflect the submission in this modal immediately.
        queryClient.setQueryData<GetEmsResponse>(['/ems/get', emsId], (prev) =>
            prev ? { ...prev, status: 'submitted' } : prev,
        );
        queryClient.setQueriesData<OrderDetailResponse>({ queryKey: ['/order/detail'] }, (prev) => {
            if (!prev?.ems?.pushes?.some(push => push._id === emsId)) return prev;
            return {
                ...prev,
                ems: {
                    ...prev.ems,
                    pushes: prev.ems.pushes.map(push =>
                        push._id === emsId ? { ...push, wasSubmitted: true } : push,
                    ),
                },
            };
        });
    }, [emsId, queryClient]);

    const pollForStatusUpdate = useCallback(() => {
        const delays = [2000, 4000, 8000];
        delays.forEach(delay => {
            setTimeout(() => {
                queryClient.invalidateQueries({ queryKey: ['/ems/get', emsId] });
                queryClient.invalidateQueries({ queryKey: ['/ems/orders/feed'] });
                queryClient.invalidateQueries({ queryKey: ['/ems/orders/feed-paginated'] });
                queryClient.invalidateQueries({ queryKey: ['/order/detail'] });
            }, delay);
        });
    }, [emsId, queryClient]);

    const handleSubmit = async () => {
        if (!data || !emsId) return;

        try {
            await updateEms.mutateAsync({ emsId });
            applyOptimisticSubmission();
            toast({
                title: "EMS Sent",
                description: "Please wait until your order populates in the Dashboard",
                status: "success",
                duration: 10000,
                isClosable: true,
            });

            onClose();
            pollForStatusUpdate();
        } catch (error: any) {
            toast({
                title: "Push Failed",
                description: error?.response?.data?.message || "Please try again.",
                status: "error",
                duration: 10000,
                isClosable: true,
            });
        }
    };

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
                <ModalOverlay />
                <ModalContent maxW="90vw" h="85vh" borderRadius="xl" display="flex" flexDirection="column" overflow="hidden">
                    <ModalCloseButton />

                    {/* ── HEADER ── */}
                    <Box bg="white" flexShrink={0} borderBottom="1px solid" borderColor="border.subtle">
                        <Flex
                            px={6}
                            pt={4}
                            pb={1}
                            align="flex-start"
                            justify="space-between"
                        >
                            {data && !error ? (
                                <VStack spacing={1.5} minW={0} mr={4} align="flex-start">
                                    <HStack spacing={2} align="center">
                                        {data.status === 'submitted' && data.orderId ? (
                                            <Link
                                                onClick={() => { navigate(`/order/${data.orderId}`); onClose(); }}
                                                _hover={{ textDecoration: 'underline', color: 'blue.600' }}
                                                cursor="pointer"
                                            >
                                                <Heading size="md" color="blue.600" lineHeight="1.25" noOfLines={1}>
                                                    {data.title}
                                                </Heading>
                                            </Link>
                                        ) : (
                                            <Heading size="md" color="gray.900" lineHeight="1.25" noOfLines={1}>
                                                {data.title}
                                            </Heading>
                                        )}
                                        <Badge
                                            colorScheme={data.status === 'submitted' ? 'green' : 'orange'}
                                            variant="subtle"
                                            px={2}
                                            py={1}
                                            fontSize="xs"
                                            fontWeight="medium"
                                            borderRadius="md"
                                            textTransform="capitalize"
                                            transition="all 0.2s"
                                        >
                                            {data.status}
                                        </Badge>
                                    </HStack>
                                    <HStack spacing={1.5} align="center">
                                        <HStack spacing={1} align="center">
                                            <Text fontSize="sm" color="gray.500" lineHeight="1.4">RO #:</Text>
                                            <Text fontSize="sm" color="gray.800" lineHeight="1.4">{data.roNumber}</Text>
                                        </HStack>
                                        <Text fontSize="sm" color="gray.300" lineHeight="1">·</Text>
                                        <HStack spacing={1} align="center">
                                            <Text fontSize="sm" color="gray.500" lineHeight="1.4">VIN:</Text>
                                            <CopyTooltip isCopied={isCopied('vin-full')}>
                                                <Text as="span" fontSize="sm" color="gray.800" lineHeight="1.4">
                                                    {data.vin.slice(0, -8)}
                                                    <CopyTooltip isCopied={isCopied('vin-last8')}>
                                                        <Text
                                                            as="span"
                                                            fontWeight="bold"
                                                            textDecoration="underline"
                                                            cursor="pointer"
                                                            _hover={{ color: 'blue.600' }}
                                                            onClick={(e) => copyToClipboard(data.vin.slice(-8), 'vin-last8', 'Last 8 of VIN', e)}
                                                        >
                                                            {data.vin.slice(-8)}
                                                        </Text>
                                                    </CopyTooltip>
                                                </Text>
                                            </CopyTooltip>
                                            <IconButton
                                                aria-label="Copy full VIN"
                                                icon={<Icon as={TbCopy as React.ElementType} w={3} h={3} />}
                                                size="xs"
                                                variant="ghost"
                                                onClick={(e) => copyToClipboard(data.vin, 'vin-full', 'Full VIN', e)}
                                                p={0.5}
                                                minW="auto"
                                                h="auto"
                                                color="gray.400"
                                                _hover={{ color: 'gray.600' }}
                                                flexShrink={0}
                                            />
                                        </HStack>
                                        <Text fontSize="sm" color="gray.300" lineHeight="1">·</Text>
                                        <HStack spacing={1} align="center">
                                            <Text fontSize="sm" color="gray.500">Shop:</Text>
                                            <Text fontSize="sm" color="gray.800">{data.location}</Text>
                                            {data.canSwitchLocation && (
                                                <Icon
                                                    as={MdEdit as React.ElementType}
                                                    boxSize={3.5}
                                                    color="gray.400"
                                                    cursor="pointer"
                                                    _hover={{ color: "#0071E3" }}
                                                    onClick={() => setIsEditLocationModalOpen(true)}
                                                />
                                            )}
                                        </HStack>
                                        <Text fontSize="sm" color="gray.300" lineHeight="1">·</Text>
                                        <HStack spacing={1} align="center">
                                            <Text fontSize="sm" color="gray.500">Insurance:</Text>
                                            <Text fontSize="sm" color="gray.800">{data.insurance}</Text>
                                        </HStack>
                                        <Text fontSize="sm" color="gray.300" lineHeight="1">·</Text>
                                        <HStack spacing={1} align="center">
                                            <Text fontSize="sm" color="gray.500">Start Date:</Text>
                                            <Text fontSize="sm" color="gray.800">
                                                {formatDate(data.timeReceived)}, {formatTime(data.timeReceived)}
                                            </Text>
                                        </HStack>
                                    </HStack>
                                </VStack>
                            ) : isLoading ? (
                                <VStack spacing={2} align="flex-start" flex={1}>
                                    <Skeleton height="24px" width="280px" borderRadius="md" />
                                    <Skeleton height="16px" width="500px" borderRadius="md" />
                                </VStack>
                            ) : null}
                        </Flex>

                        {data && data.needsQuoteRequestId && (
                            <Box mx={6} my={2}>
                                <QuotesNotSubmitted
                                    onClick={() => {
                                        navigate(`/order/${data.needsQuoteRequestId}`, { state: { initialStatus: 'newParts' } });
                                        onClose();
                                    }}
                                />
                            </Box>
                        )}

                        {/* ── "TAB BAR" – Parts List heading + actions ── */}
                        <Flex px={6} py={3} align="center" justify="space-between">
                            <SegmentedControl
                                size="sm"
                                value={showPartsOnly ? 'parts' : 'all'}
                                onChange={(next) => setShowPartsOnly(next === 'parts')}
                                items={[
                                    { value: 'all', label: 'All' },
                                    { value: 'parts', label: 'Parts' },
                                ]}
                            />
                            {showSubmitActions && (
                                <HStack spacing={3}>
                                    {showMarketplaceBadges && (
                                        <HStack spacing={2}>
                                            {data.marketplaces.map(marketplace => (
                                                <Box
                                                    key={marketplace.marketplace}
                                                    display="flex"
                                                    alignItems="center"
                                                    gap={1}
                                                    px={2.5}
                                                    py={1}
                                                    border="1px solid"
                                                    borderColor="green.500"
                                                    borderRadius="full"
                                                    bg="green.50"
                                                    color="green.500"
                                                    fontSize="xs"
                                                    fontWeight="medium"
                                                >
                                                    <Icon as={MdCheck as React.ElementType} boxSize={3} />
                                                    {marketplace.displayName}
                                                </Box>
                                            ))}
                                        </HStack>
                                    )}
                                    {!shouldHideSubmit && (
                                        <PartBayButton
                                            colorScheme="partbay-blue"
                                            variant="solid"
                                            size="sm"
                                            text={`${data.status === 'submitted' ? 'Re-' : ''}Send to Marketplaces`}
                                            onClick={handleSubmit}
                                            isLoading={updateEms.isPending}
                                            isDisabled={!data || data.numMarketplaces === 0}
                                        />
                                    )}
                                </HStack>
                            )}
                        </Flex>
                    </Box>

                    {/* ── SCROLLABLE CONTENT ── */}
                    <Box flex={1} overflow="hidden" px={6} pt={4} pb={4} bg="white">
                        {isLoading ? (
                            <Box border="1px solid" borderColor="border.subtle" borderRadius="lg" overflow="hidden" bg="white">
                                <Flex bg="bg.subtle" p={3} borderBottom="1px solid" borderColor="border.subtle">
                                    {['30px', '50px', '40px', '70px', '90px', '50px'].map((w, i) => (
                                        <Skeleton key={i} height="14px" width={w} flex={i === 3 ? 2 : 1} mr={i < 5 ? 2 : 0} borderRadius="md" />
                                    ))}
                                </Flex>
                                {Array(6).fill(0).map((_, i) => (
                                    <Flex key={i} p={3} borderBottom="1px solid" borderColor="gray.100" align="center">
                                        <Skeleton height="16px" width="16px" mr={4} borderRadius="md" />
                                        <Skeleton height="16px" width="30px" flex={1} mr={2} borderRadius="md" />
                                        <Skeleton height="16px" width="20px" flex={1} mr={2} borderRadius="md" />
                                        <Skeleton height="24px" width="45px" flex={1} mr={2} borderRadius="md" />
                                        <Skeleton height="16px" width="70%" flex={2} mr={2} borderRadius="md" />
                                        <Skeleton height="16px" width="80px" flex={1} mr={2} borderRadius="md" />
                                        <Skeleton height="16px" width="60px" flex={1} borderRadius="md" />
                                    </Flex>
                                ))}
                            </Box>
                        ) : error || !data ? (
                            <Alert status="error">
                                <AlertIcon />
                                Failed to load EMS data. Please try again.
                            </Alert>
                        ) : (
                            <Box
                                border="1px solid"
                                borderColor="border.subtle"
                                borderRadius="lg"
                                overflow="hidden"
                                h="100%"
                                display="flex"
                                flexDirection="column"
                                bg="white"
                            >
                                <Box
                                    flex={1}
                                    overflowY="auto"
                                    bg="white"
                                    sx={{
                                        '&::-webkit-scrollbar': { width: '8px' },
                                        '&::-webkit-scrollbar-track': { background: 'transparent' },
                                        '&::-webkit-scrollbar-thumb': { background: '#cbd5e0', borderRadius: '4px' },
                                        '&::-webkit-scrollbar-thumb:hover': { background: '#a0aec0' },
                                        scrollbarWidth: 'thin',
                                        scrollbarColor: '#cbd5e0 transparent',
                                    }}
                                >
                                    <Table variant="simple" size="md">
                                        <Thead position="sticky" top={0} bg="bg.subtle" zIndex={1} boxShadow="inset 0 -1px 0 0 var(--chakra-colors-border-subtle)">
                                            <Tr>
                                                <Th py={3} px={4} color="fg.muted" fontWeight="semibold" fontSize="xs" letterSpacing="wide" textTransform="uppercase">Line #</Th>
                                                <Th py={3} px={4} color="fg.muted" fontWeight="semibold" fontSize="xs" letterSpacing="wide" textTransform="uppercase">Qty</Th>
                                                <Th py={3} px={4} color="fg.muted" fontWeight="semibold" fontSize="xs" letterSpacing="wide" textTransform="uppercase">Part Type</Th>
                                                <Th py={3} px={4} color="fg.muted" fontWeight="semibold" fontSize="xs" letterSpacing="wide" textTransform="uppercase">Description</Th>
                                                <Th py={3} px={4} color="fg.muted" fontWeight="semibold" fontSize="xs" letterSpacing="wide" textTransform="uppercase">OE Part #</Th>
                                                <Th py={3} px={4} color="fg.muted" fontWeight="semibold" fontSize="xs" letterSpacing="wide" textTransform="uppercase" isNumeric>Price</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {filteredParts.map((part, i) => (
                                                <Tr
                                                    key={`${part.partNumber}-${i}`}
                                                    borderBottom="1px solid"
                                                    borderColor="gray.100"
                                                >
                                                    <Td px={4} color="gray.500" fontWeight="medium">{part.lineNumber}</Td>
                                                    <Td px={4} color="gray.500" fontWeight="medium">{part.quantity}</Td>
                                                    <Td px={4}><PartType partType={part.type} /></Td>
                                                    <Td px={4} color="gray.500" fontWeight="semibold">{part.partName}</Td>
                                                    <Td px={4} color="gray.500" fontWeight="medium">{part.partNumber}</Td>
                                                    <Td px={4} color="gray.500" fontWeight="semibold" isNumeric>{formatPrice(part.listPrice)}</Td>
                                                </Tr>
                                            ))}
                                        </Tbody>
                                    </Table>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </ModalContent>
            </Modal>

            <EditLocationModal
                isOpen={isEditLocationModalOpen}
                onClose={() => setIsEditLocationModalOpen(false)}
                onParentClose={onClose}
                emsId={emsId}
            />
        </>
    );
};

export default EMSDetailModal;